import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { EVENT_MEINE_NACHRICHTEN, InitialState, MNRContentResponse, MNRTrackOpenMessageData, MNRUserMessagesResponse } from '@mwe/models';
import { computed, inject } from '@angular/core';
import { MnrMessageService } from '../mnr-message.service';
import { OutputData } from '@editorjs/editorjs/types/data-formats/output-data';
import { track } from '@mwe/utils';

export type MessagesStoreType = InstanceType<typeof MessagesStore>;

export const MessagesStore = signalStore(
  { providedIn: 'root' },
  withState<InitialState>({
    messages: [],
    selectedMessage: undefined,
    hasError: false,
    showDetails: false,
    errorMessage: '',
    isDisabled: false,
  }),
  withComputed(({ messages, selectedMessage }) => ({
    unreadMessageCount: computed<string>(() => {
      const unreadMessages = messages().filter(message => message.readDate == null);
      if (unreadMessages.length === 0) return '';
      if (unreadMessages.length <= 9) return `${unreadMessages.length}`;

      return '9+';
    }),
    messageContent: computed<OutputData>(() => {
      const currentContent = selectedMessage();
      if (currentContent?.contentDataJson) {
        return currentContent?.contentDataJson ?? ({} as OutputData);
      }

      const data = JSON.parse(currentContent?.contentData) as OutputData;

      if (!data) return {} as OutputData;
      return data;
    }),
  })),
  withMethods((store, messageService = inject(MnrMessageService)) => ({
    updateMessages(messages: MNRUserMessagesResponse[]): void {
      patchState(store, state => ({ ...state, messages }));
    },
    updateSelectedMessage(selectedMessage: MNRContentResponse | undefined): void {
      patchState(store, state => ({ ...state, selectedMessage }));
    },
    updateSelectedMessageContent(contentDataJson: OutputData): void {
      patchState(store, state => ({
        ...state,
        selectedMessage: {
          ...state.selectedMessage,
          contentDataJson,
        },
      }));
    },
    toggleShowDetails(): void {
      patchState(store, state => ({ ...state, showDetails: !state.showDetails }));
    },
    setErrorMessage(errorMessage: string, hasError = true): void {
      patchState(store, state => ({ ...state, hasError, errorMessage }));
    },
    setShowDetails(showDetails: boolean): void {
      patchState(store, state => ({ ...state, showDetails }));
    },
    async loadMessages(): Promise<void> {
      try {
        const messages = await messageService.getUserMessages();
        messages.sort((a, b) => {
          return new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime();
        });
        patchState(store, state => ({ ...state, messages }));
      } catch {
        patchState(store, state => ({ ...state, hasError: true, errorMessage: 'mnr.overlay.error.title-plural' }));
      }
    },
    async loadMessage(id: number): Promise<void> {
      try {
        const selectedMessage = await messageService.getMessageContent(id);
        patchState(store, state => ({ ...state, selectedMessage, showDetails: true }));
        const messageHeader = store.messages()?.find(e => e.messageID === id);
        if (messageHeader) {
          track(
            EVENT_MEINE_NACHRICHTEN,
            new MNRTrackOpenMessageData(
              messageHeader.subject,
              messageHeader.messageID?.toString(),
              messageHeader.messageName,
              messageHeader.readDate === null,
            ),
          );
        }
      } catch {
        patchState(store, state => ({
          ...state,
          hasError: true,
          showDetails: true,
          errorMessage: 'mnr.overlay.error.title-singular',
        }));
      }
    },
    async markAsRead(id: number) {
      try {
        await messageService.markMessageAsRead(id);
        /**
         * Update current Message Pool to avoid refetching all messages.
         * Messages should be updated on reload
         */
        patchState(store, state => {
          const newMessages = state.messages.map(message => {
            if (message.messageID === id) {
              message.readDate = new Date().toISOString();
            }
            return message;
          });

          return {
            ...state,
            messages: newMessages,
          };
        });
      } catch {
        /* empty */
      }
    },
  })),
);
